// src/pages/teams/ViewTeam.jsx
// Team detail page — rebuilt around registration management as the primary use case.

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  doc, onSnapshot, collection, query, where,
  getCountFromServer, getDocs, orderBy, limit,
  updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';
import useEntitlements from '@/hooks/useEntitlements';
import { useRole } from '@/context/RoleContext';
import TeamSubNav from '@/components/team/TeamSubNav';
import TeamSmartActions from '@/components/team/TeamSmartActions';
import DisengagedParentCard from '@/components/team/DisengagedParentCard';
import TeamCommunicationHub from '@/components/team/TeamCommunicationHub';
import TeamActivityFeed from '@/components/team/TeamActivityFeed';
import TeamSetupNotice from '@/components/TeamSetupNotice';
import clsx from 'clsx';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { toSeasonDate, endOfSeasonDay } from '@/utils/seasonDates';

import { Skeleton } from '@/components/skeleton';
// Send-to-Team CTA — same component the Dashboard uses so the menu
// behaves identically across the app. variant="darkOutlined" + size="sm"
// adapts the styling for the pitch-dark team hero.
import SendToTeamMenu from '@/components/dashboard/SendToTeamMenu';
// Onboarding checklist — same component used on Dashboard. Mounted
// here too so a coach who finishes the wizard and lands on the team
// page sees clear next steps (Add roster / Create season / Invite
// staff) without having to navigate back to Dashboard to find guidance.
import OnboardingChecklist from '@/components/dashboard/OnboardingChecklist';
import { sportLabel, genderLabel } from '@/utils/displayLabels';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

function SectionLabel({ children, className = '' }) {
  return (
    <p
      className={`text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] ${className}`}
      style={{ fontFamily: HEAD_FONT }}
    >
      {children}
    </p>
  );
}

function GhostButton({ children, onClick, className = '', as: As = 'button', type = 'button', ...rest }) {
  return (
    <As
      type={As === 'button' ? type : undefined}
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-1.5 rounded-lg border border-zinc-300 bg-white hover:bg-zinc-50 hover:border-[#b5f03d]/40 px-3.5 py-2 text-[13px] font-semibold text-[#0d1f14] transition-colors ${className}`}
      {...rest}
    >
      {children}
    </As>
  );
}
import {
  PencilSquareIcon,
  CalendarDaysIcon,
  CheckBadgeIcon,
  TrophyIcon,
  ChartBarIcon,
  ChevronRightIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  PlusIcon,
  ArrowRightIcon,
  BuildingOffice2Icon,
  PaperAirplaneIcon,
  DocumentCheckIcon,
  ExclamationCircleIcon,
  RectangleGroupIcon,
  MapPinIcon,
  SparklesIcon,
  EnvelopeOpenIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

/* ─── helpers ────────────────────────────────────────────────────── */

function formatMoney(cents) {
  if (!cents) return '$0';
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatClose(val) {
  const d = toSeasonDate(val);
  try { return d ? format(d, 'MMM d') : null; } catch { return null; }
}

/* ─── sub-components ─────────────────────────────────────────────── */
function MiniStat({ label, value, icon: Icon, tone = 'lime', to }) {
  const tones = {
    lime:   { bg: 'bg-[#b5f03d]/15 ring-[#b5f03d]/40', icon: 'text-[#5a8a1f]' },
    emerald:{ bg: 'bg-emerald-50 ring-emerald-100',    icon: 'text-emerald-600' },
    amber:  { bg: 'bg-amber-50 ring-amber-100',        icon: 'text-amber-600' },
  };
  const c = tones[tone] || tones.lime;
  const inner = (
    <div className="rounded-xl border border-zinc-200 bg-white p-3.5 hover:border-[#b5f03d]/40 hover:shadow-[0_4px_12px_-4px_rgba(13,31,20,0.08)] transition-all h-full">
      <div className={clsx('inline-flex items-center justify-center rounded-lg ring-1 p-1.5 mb-2', c.bg)}>
        <Icon className={clsx('h-4 w-4', c.icon)} />
      </div>
      <p className="text-2xl font-bold text-[#0d1f14] leading-none" style={{ fontFamily: HEAD_FONT, letterSpacing: '0.2px' }}>{value ?? '—'}</p>
      <p className="text-[11px] font-bold uppercase tracking-wide text-zinc-500 mt-1">{label}</p>
    </div>
  );
  return to ? <Link to={to} className="block">{inner}</Link> : <div>{inner}</div>;
}

function QuickLink({ to, icon: Icon, label, sub }) {
  return (
    <Link to={to} className="group flex items-center gap-3 rounded-xl border border-zinc-200 bg-white px-3.5 py-3 transition-all hover:border-[#b5f03d]/40 hover:bg-[#b5f03d]/5">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 text-[#5a8a1f]">
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#0d1f14]">{label}</p>
        {sub && <p className="text-xs text-zinc-500 mt-0.5 truncate">{sub}</p>}
      </div>
      <ChevronRightIcon className="h-4 w-4 text-zinc-300 group-hover:text-[#5a8a1f] shrink-0 transition-colors" />
    </Link>
  );
}

function ActiveSeasonCard({ season, regCount, revenue, teamId }) {
  const { t } = useTranslation();
  const [opening, setOpening] = useState(false);
  const closeDate  = formatClose(season.registrationClose);
  const maxPlayers = season.maxPlayers || null;
  const pct        = maxPlayers && regCount != null
    ? Math.min(100, Math.round((regCount / maxPlayers) * 100))
    : null;

  // D10: the auto season arrives as a DRAFT — badge flips to amber and
  // the primary action becomes the one-tap open (mirrors the Home
  // checklist). Share Invite is meaningless pre-open (the public page
  // shows closed), so it's hidden until the coach opens.
  const isDraft = season.status === 'draft';
  const openSeason = async () => {
    if (opening) return;
    setOpening(true);
    try {
      await updateDoc(doc(db, 'seasons', season.id), {
        status: 'open',
        updatedAt: serverTimestamp(),
      });
      toast.success(t('teamDetail.seasonOpenedToast'));
    } catch {
      toast.error(t('teamDetail.seasonOpenFailedToast'));
    } finally {
      setOpening(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[#b5f03d]/40 bg-gradient-to-br from-[#b5f03d]/8 to-white overflow-hidden shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]">
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={
                isDraft
                  ? 'inline-flex items-center rounded-full bg-amber-100 ring-1 ring-amber-300 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[1.2px] text-amber-700 shrink-0'
                  : 'inline-flex items-center rounded-full bg-[#b5f03d]/20 ring-1 ring-[#b5f03d]/40 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[1.2px] text-[#3d6010] shrink-0'
              }
              style={{ fontFamily: HEAD_FONT }}
            >
              {isDraft ? t('teamDetail.draftSeason') : t('teamDetail.openSeason')}
            </span>
            <h3 className="text-lg font-bold text-[#0d1f14] truncate" style={{ fontFamily: HEAD_FONT, letterSpacing: '0.2px' }}>{season.name}</h3>
          </div>
          <Link
            to={`/seasons/${season.id}/registrations`}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#5a8a1f] hover:text-[#3d6010] transition-colors shrink-0"
          >
            {t('teamDetail.seeWhoSignedUp')} <ArrowRightIcon className="h-4 w-4" />
          </Link>
        </div>

        <p className="mt-2 text-xs text-zinc-500 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {closeDate && <span>{t('teamDetail.closesOn', { date: closeDate })}</span>}
          {closeDate && <span className="text-zinc-300">·</span>}
          <span>
            <span className="font-semibold text-zinc-700">{regCount ?? 0}</span>
            {' '}{t('teamDetail.signedUpSuffix')}
          </span>
          {revenue != null && (
            <>
              <span className="text-zinc-300">·</span>
              <span>
                <span className="font-semibold text-zinc-700">{formatMoney(revenue)}</span> {t('teamDetail.collectedSuffix')}
              </span>
            </>
          )}
        </p>

        {pct !== null && (
          <div className="mt-3">
            <div className="h-1.5 w-full rounded-full bg-[#b5f03d]/15 overflow-hidden">
              <div className="h-full rounded-full bg-[#94cc28] transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-1 text-[10px] text-zinc-500">{t('teamDetail.capacityFilled', { pct })}</p>
          </div>
        )}

        {/* Draft state must explain its CONSEQUENCE, not just its name —
            "Draft" is builder vocabulary; "parents can't join yet" is
            the reason to press the button. */}
        {isDraft && (
          <p className="mt-2 text-xs font-medium text-amber-700">
            {t('teamDetail.draftExplainer')}
          </p>
        )}
      </div>

      <div className="border-t border-[#b5f03d]/30 px-5 py-3 flex flex-wrap items-center gap-2">
        {isDraft ? (
          <>
            <button
              type="button"
              onClick={openSeason}
              disabled={opening}
              className={'inline-flex items-center gap-1.5 rounded-[10px] bg-[#b5f03d] hover:bg-[#94cc28] px-3 py-1.5 text-xs font-bold text-[#0d1f14] uppercase transition-colors' + (opening ? ' opacity-60' : '')}
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              <CheckBadgeIcon className="h-3.5 w-3.5" /> {t('teamDetail.openRegistration')}
            </button>
            <Link
              to={`/seasons/${season.id}`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-xs font-semibold text-[#0d1f14] hover:bg-zinc-50 hover:border-[#b5f03d]/40 transition-colors"
            >
              {t('teamDetail.reviewSeason')}
            </Link>
          </>
        ) : (
        <Link
          to={`/seasons/${season.id}`}
          className="inline-flex items-center gap-1.5 rounded-[10px] bg-[#b5f03d] hover:bg-[#94cc28] px-3 py-1.5 text-xs font-bold text-[#0d1f14] uppercase transition-colors"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
        >
          <PaperAirplaneIcon className="h-3.5 w-3.5" /> {t('teamDetail.shareInvite')}
        </Link>
        )}
        <Link
          to={`/seasons/${season.id}/registrations`}
          className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-xs font-semibold text-[#0d1f14] hover:bg-zinc-50 hover:border-[#b5f03d]/40 transition-colors"
        >
          <DocumentCheckIcon className="h-3.5 w-3.5" /> {t('teamDetail.whosMissingForms')}
        </Link>
        <Link
          to="/reports/financial"
          className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-xs font-semibold text-[#0d1f14] hover:bg-zinc-50 hover:border-[#b5f03d]/40 transition-colors"
        >
          <ChartBarIcon className="h-3.5 w-3.5" /> {t('teamDetail.financials')}
        </Link>
        {/* "+ New season" — visible escape hatch for coaches who want to
            create a second/third season (Spring after Fall, etc.). Without
            this, the only path to /seasons/create after the team has one
            season was through the "Seasons" Quick Tools tile, which is a
            cross-team admin surface that's not obvious from a team page.
            ml-auto pushes it to the right end of the row to mark it as a
            secondary "add another" action vs the primary in-season actions
            on the left. */}
        <Link
          to="/seasons/create"
          className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-xs font-semibold text-[#5a8a1f] hover:bg-[#b5f03d]/10 hover:border-[#b5f03d]/40 transition-colors"
        >
          <PlusIcon className="h-3.5 w-3.5" /> {t('teamDetail.newSeason')}
        </Link>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-8 animate-pulse">
      <div className="flex gap-4">
        <Skeleton className="h-20 w-20 rounded-2xl" />
        <div className="space-y-2 flex-1">
          <Skeleton className="h-7 w-56 rounded-lg" />
          <Skeleton className="h-4 w-40 rounded" />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <Skeleton className="h-44 w-full rounded-2xl" />
          <Skeleton className="h-28 w-full rounded-2xl" />
        </div>
        <div className="space-y-5">
          <Skeleton className="h-28 w-full rounded-2xl" />
          <Skeleton className="h-44 w-full rounded-2xl" />
        </div>
      </div>
    </div>
  );
}

/* ─── main ───────────────────────────────────────────────────────── */
export default function ViewTeam() {
  const { t, i18n } = useTranslation();
  const { teamId }  = useParams();
  const navigate    = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { firebaseUser, user: authUser, userProfile } = useAuth();
  // Soft Gate (May 2026): hide club-tier features (Programs tile) for
  // free-tier users. During Phase 1 founding cohort isClub returns
  // true for everyone, so this is wired but not yet enforcing. See
  // useEntitlements.js PHASE_1_UNLOCK_ALL flag.
  const { isClub } = useEntitlements(firebaseUser?.uid || null);
  const uid = firebaseUser?.uid || authUser?.uid || authUser?.id;

  /* ── core state ── */
  const [team,     setTeam]     = useState(null);
  const [loading,  setLoading]  = useState(true);

  // Per-team role detection comes from RoleContext — the single
  // source of truth shared with the sidebar. teamRole returns
  // 'coach' for owner/staff, 'parent' for parent-only, null
  // otherwise. The "no global mode" rule (Phase 1 refactor): role
  // is a property of the team relationship, not the user. So a
  // user who is COACH on this team gets the coach view regardless
  // of whether their profile.isParent flag is true elsewhere
  // (e.g. they're a parent on a different team, or they finished
  // parent intake). Only check teamRole here.
  const { teamRole } = useRole();
  const isParent = teamRole(teamId) === 'parent';

  /* ── season / registration state ── */
  const [activeSeason,   setActiveSeason]   = useState(null);
  const [activeRegCount, setActiveRegCount] = useState(null);
  // Smart-action inputs (added May 2026 — restoring the assistant-coach
  // surface that was lost during Phase 1). daysUntilClose drives the
  // "registration closing soon" prompt; missingDocsCount drives the
  // "N players missing required documents" prompt.
  const [daysUntilClose,  setDaysUntilClose]  = useState(null);
  const [missingDocsCount, setMissingDocsCount] = useState(null);
  const [activeRevenue,  setActiveRevenue]  = useState(null);
  const [unpaidCount,    setUnpaidCount]    = useState(null);
  const [rosterCount,    setRosterCount]    = useState(null);

  /* ── onboarding state ──
     Drives the OnboardingChecklist mounted below the team header
     for coaches. Parents don't see it (they didn't create the team).
     We load invite count once per team mount; roster + season state
     come from the existing per-team listeners further down. */
  const [inviteCount, setInviteCount] = useState(null);
  useEffect(() => {
    if (!teamId || isParent) { setInviteCount(null); return; }
    getCountFromServer(query(collection(db, 'invites'), where('teamId', '==', teamId)))
      .then(snap => setInviteCount(snap.data().count))
      .catch(() => setInviteCount(0));
  }, [teamId, isParent]);

  /* Activation signal for the checklist's step 4 — has this team ever
     actually SENT a message? Either a composer message flipped to 'sent'
     or a broadcast audit doc counts (mirrors the Home checklist and the
     admin stage ladder). Fail-quiet to false: showing the step as not
     done is the safe direction.
     Tri-state: null = still checking. The pre-activation collapse below
     only engages at an explicit false, so an activated coach never sees
     their tools flash away while the query resolves. */
  const [hasMessage, setHasMessage] = useState(null);
  // Pre-activation escape hatch: "More tools" reveals the full layout.
  const [showMoreTools, setShowMoreTools] = useState(false);
  useEffect(() => {
    if (!teamId || isParent) { setHasMessage(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const sent = await getDocs(query(
          collection(db, 'teams', teamId, 'messages'),
          where('status', '==', 'sent'),
          limit(1),
        ));
        if (!cancelled && !sent.empty) { setHasMessage(true); return; }
      } catch { /* fall through */ }
      try {
        const bc = await getDocs(query(
          collection(db, 'teams', teamId, 'broadcasts'),
          limit(1),
        ));
        if (!cancelled) setHasMessage(!bc.empty);
      } catch {
        if (!cancelled) setHasMessage(false);
      }
    })();
    return () => { cancelled = true; };
  }, [teamId, isParent]);

  /* ── welcome=1 query param ──
     The team-setup wizard navigates here with ?welcome=1 after a
     successful finishSetup. We surface a one-time congrats toast,
     then strip the param from the URL so it doesn't fire again on
     refresh / back-button. The persistent OnboardingChecklist below
     handles the actual "what to do next" guidance. */
  useEffect(() => {
    if (searchParams.get('welcome') !== '1') return;
    toast.success(t('teamDetail.teamCreatedToast'), {
      duration: 5000,
      icon: '🎉',
    });
    const next = new URLSearchParams(searchParams);
    next.delete('welcome');
    setSearchParams(next, { replace: true });
    // Intentionally only run once per landing — even though
    // searchParams is in the dep array, clearing it inside the effect
    // makes this self-terminating after one fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Dismiss handler for OnboardingChecklist — writes the flag on
     the user doc so once dismissed it stays dismissed everywhere
     (Dashboard, every team detail page). Same field name Dashboard
     uses, so the two surfaces stay in sync. */
  const dismissOnboarding = async () => {
    if (!uid) return;
    try {
      await updateDoc(doc(db, 'users', uid), {
        onboardingDismissed: true,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.warn('[ViewTeam] dismiss onboarding failed', e);
    }
  };

  // Coach-only. Hide entirely once user dismisses or every step
  // is complete (the checklist component itself returns null at 4/4).
  const showOnboardingChecklist =
    !isParent && uid && !userProfile?.onboardingDismissed;

  /* ── fetch team ── */
  useEffect(() => {
    if (!teamId) return;
    setLoading(true);
    return onSnapshot(doc(db, 'teams', teamId), (snap) => {
      if (!snap.exists()) { toast.error(t('teamDetail.teamNotFound')); navigate('/teams'); return; }
      const data = { id: snap.id, ...snap.data() };
      setTeam(data);
      setLoading(false);
    }, () => setLoading(false));
  }, [teamId, navigate]);

  /* ── fetch roster count ──
       Coaches read from `players` (full PII); parents read from
       `playersPublic` (safe mirror) since rules block parent access
       to the full doc. Both collections have one doc per player so
       the count is identical. */
  useEffect(() => {
    if (!teamId) return;
    const subcollection = isParent ? 'playersPublic' : 'players';
    getCountFromServer(collection(db, 'teams', teamId, subcollection))
      .then(snap => setRosterCount(snap.data().count))
      .catch(() => setRosterCount(0));
  }, [teamId, isParent]);

  /* ── fetch active season + registration stats ── */
  useEffect(() => {
    if (!teamId) return;
    const q = query(
      collection(db, 'seasons'),
      where('teamId', '==', teamId),
      // Drafts included since D10 (auto season starts draft): the card
      // must show it with its "open registration" action, otherwise the
      // team page claims "no open season" while Home says otherwise.
      where('status', 'in', ['open', 'draft']),
      orderBy('createdAt', 'desc'),
      limit(1),
    );
    return onSnapshot(q, async (snap) => {
      if (snap.empty) {
        setActiveSeason(null);
        setActiveRegCount(null);
        setActiveRevenue(null);
        setUnpaidCount(null);
        setDaysUntilClose(null);
        setMissingDocsCount(null);
        return;
      }
      const season = { id: snap.docs[0].id, ...snap.docs[0].data() };
      setActiveSeason(season);

      // Days until registration close (smart-action input). Negative
      // means it's already closed — clamp to 0 so the action's
      // "closing in 0 days" branch never shows for past windows.
      if (season.registrationClose) {
        try {
          const closeDate = endOfSeasonDay(season.registrationClose);
          const days = Math.ceil((closeDate - Date.now()) / (1000 * 60 * 60 * 24));
          setDaysUntilClose(days > 0 ? days : 0);
        } catch { setDaysUntilClose(null); }
      } else {
        setDaysUntilClose(null);
      }

      try {
        const regsSnap = await getDocs(query(
          collection(db, 'registrations'),
          where('teamId',   '==', season.teamId),
          where('seasonId', '==', season.id),
        ));
        setActiveRegCount(regsSnap.size);

        const requiredDocs = season.requiredDocTypes || [];
        let revenue = 0, unpaid = 0, missingDoc = 0;
        regsSnap.forEach(d => {
          const r = d.data();
          const amt = r.pricing?.finalAmount ?? r.amountDue ?? 0;
          if (r.status === 'confirmed' || r.status === 'paid') revenue += amt;
          if (amt > 0 && !['paid', 'confirmed', 'cancelled'].includes(r.status)) unpaid++;
          if (requiredDocs.length > 0) {
            const uploaded = r.uploadedDocs || {};
            const anyMissing = requiredDocs.some(dt => !uploaded[dt]);
            if (anyMissing) missingDoc++;
          }
        });
        setActiveRevenue(revenue);
        setUnpaidCount(unpaid);
        setMissingDocsCount(missingDoc);
      } catch {
        setActiveRegCount(0);
        setActiveRevenue(0);
        setUnpaidCount(0);
        setMissingDocsCount(0);
      }
    });
  }, [teamId]);

  /* ── permissions ── */
  const isOwner  = !!uid && !!team && (team.coachId === uid || team.ownerId === uid);
  const canEdit  = isOwner;

  if (loading) return <LoadingSkeleton />;
  if (!team)   return <div className="p-10 text-center text-zinc-500">{t('teamDetail.teamNotFound')}</div>;

  /* ── header meta: only show fields that have values. Sport/gender are
     stored English values — localized for display only. ── */
  const metaParts = [
    sportLabel(team.sport, i18n?.language),
    team.ageGroup,
    genderLabel(team.gender, i18n?.language),
    team.skillLevel,
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-[#f7faf5]">
    {/* Standardized container — same max-w-5xl + padding as RosterPage,
        TeamSeasonsList, and SeasonSchedule so the four team sub-pages
        feel like one consistent surface when the user tabs between them
        via TeamSubNav. */}
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8">

      {/* Team-page sub-navigation — Overview / Roster / Schedule /
          Chat. Role-aware (parents see fewer tabs). Sticky to the
          top of the scroll container so it's always reachable. */}
      <TeamSubNav teamId={teamId} />

      {/* ── HEADER (pitch-dark card matching EventDetailPage) ─── */}
      <div className="mb-6 bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
        {/* Pitch-dark header with lime-ringed logo medallion */}
        <div className="px-5 pt-5 pb-5 bg-[#0d1f14] relative overflow-hidden">
          {/* Subtle lime grid pattern */}
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                'linear-gradient(to right, #b5f03d 1px, transparent 1px), linear-gradient(to bottom, #b5f03d 1px, transparent 1px)',
              backgroundSize: '32px 32px',
            }}
          />
          <div className="relative flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              {/* Lime-ringed logo circle */}
              <div className="flex h-14 w-14 sm:h-16 sm:w-16 shrink-0 items-center justify-center rounded-full bg-[#b5f03d]/15 ring-2 ring-[#b5f03d]/40 overflow-hidden">
                {(team.logoUrl || team.clubLogoUrl) ? (
                  <img
                    src={team.logoUrl || team.clubLogoUrl}
                    alt={team.name}
                    className="h-full w-full object-cover rounded-full"
                  />
                ) : (
                  <span
                    className="text-xl sm:text-2xl font-bold text-[#b5f03d] uppercase"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    {team.name?.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="min-w-0">
                <p
                  className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#b5f03d] mb-1"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  {t('teamDetail.teamLabel')}
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1
                    className="uppercase text-white leading-[1.05] truncate"
                    style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 26, letterSpacing: '0.5px' }}
                  >
                    {team.name}
                  </h1>
                  {team.trustBadges?.includes('verified_club') && (
                    <CheckBadgeIcon className="h-5 w-5 text-[#b5f03d] shrink-0" title={t('teamDetail.verifiedClub')} />
                  )}
                </div>
                {metaParts.length > 0 && (
                  <p className="mt-1 text-sm text-white/70">
                    {metaParts.join(' · ')}
                  </p>
                )}
                {(team.clubName || team.clubId) && (
                  <span className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[#b5f03d]/40 bg-[#b5f03d]/10 px-2.5 py-0.5 text-[11px] font-semibold text-[#b5f03d]">
                    <BuildingOffice2Icon className="h-3 w-3" />
                    {team.clubName || t('teamDetail.clubTeam')}
                  </span>
                )}
              </div>
            </div>

            {canEdit && (
              <div className="flex flex-wrap gap-2 shrink-0">
                {/* Replaces the standalone "Chat" button. The menu
                    surfaces both Team Chat and AI Broadcast from one
                    place — same pattern as the Dashboard header so
                    coaches don't have to relearn navigation per page. */}
                <SendToTeamMenu
                  teamId={team.id}
                  size="sm"
                  variant="darkOutlined"
                  label={t('teamDetail.send')}
                  align="right"
                />
                <button
                  type="button"
                  onClick={() => navigate(`/teams/${team.id}/edit`)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#b5f03d]/40 bg-[#b5f03d]/10 hover:bg-[#b5f03d]/20 text-[#b5f03d] text-xs font-bold uppercase tracking-wide transition-colors"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                >
                  <PencilSquareIcon className="h-3.5 w-3.5" /> {t('teamDetail.edit')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── ONBOARDING CHECKLIST ──────────────────────────────────────
           Shown only for coaches who haven't dismissed it. The
           component auto-hides at 4/4 done, so we don't need to
           guard against that. Steps are derived from real Firestore
           state (rosterCount, activeSeason, inviteCount) so a coach
           who completes a step in another tab sees it tick here too.

           Lives between the team header and the main detail content
           so it's the first thing a freshly-onboarded coach sees
           (the wizard ends with a navigate to /teams/{id}?welcome=1,
           which lands on this page). */}
      {showOnboardingChecklist && (
        <OnboardingChecklist
          hasTeam={true /* you're on the team page, so this is by definition true */}
          hasRoster={(rosterCount ?? 0) > 0}
          hasRegistration={(activeRegCount ?? 0) > 0}
          hasMessage={!!hasMessage}
          hasSeason={activeSeason?.status === 'open'}
          hasInvite={(inviteCount ?? 0) > 0}
          teamId={teamId}
          onDismiss={dismissOnboarding}
        />
      )}

      {/* ── PARENT VIEW ─ stripped-down: schedule, roster, chat ── */}
      {isParent ? (
        <div className="space-y-5">
          {activeSeason ? (
            <Link
              to={`/seasons/${activeSeason.id}/schedule`}
              className="block rounded-2xl border border-zinc-200 bg-white p-5 hover:border-[#b5f03d]/40 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 flex items-center justify-center shrink-0">
                  <CalendarDaysIcon className="h-5 w-5 text-[#5a8a1f]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]" style={{ fontFamily: HEAD_FONT }}>
                    {t('teamDetail.currentSeason')}
                  </p>
                  <p className="text-sm font-semibold text-[#0d1f14] truncate">{activeSeason.name || t('teamDetail.seasonFallback')}</p>
                </div>
                <span className="text-xs font-semibold text-[#5a8a1f] shrink-0 group-hover:translate-x-0.5 transition-transform">
                  {t('teamDetail.viewScheduleArrow')}
                </span>
              </div>
            </Link>
          ) : (
            <div className="rounded-2xl border border-dashed border-zinc-200 bg-white p-8 text-center">
              <div className="mx-auto h-10 w-10 rounded-full bg-zinc-100 flex items-center justify-center mb-3">
                <CalendarDaysIcon className="h-5 w-5 text-zinc-400" />
              </div>
              <p className="text-sm font-semibold text-[#0d1f14] mb-1">{t('teamDetail.noActiveSeason')}</p>
              <p className="text-xs text-zinc-500">
                {t('teamDetail.parentNoSeasonHint')}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <SectionLabel className="px-0.5">{t('teamDetail.team')}</SectionLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {activeSeason && (
                <QuickLink
                  to={`/seasons/${activeSeason.id}/schedule`}
                  icon={CalendarDaysIcon}
                  label={t('teamDetail.schedule')}
                  sub={t('teamDetail.gamesAndPractices')}
                />
              )}
              <QuickLink
                to={teamId ? `/teams/${teamId}/chat` : '/messages'}
                icon={ChatBubbleLeftRightIcon}
                label={t('teamDetail.teamChat')}
                sub={t('teamDetail.messagesFromCoach')}
              />
              <QuickLink
                to={teamId ? `/teams/${teamId}/roster` : '/teams'}
                icon={UserGroupIcon}
                label={t('teamDetail.roster')}
                sub={rosterCount != null ? t('teamDetail.playerCount', { count: rosterCount }) : t('teamDetail.seeTeammates')}
              />
            </div>
          </div>
        </div>
      ) : (
      /* ── COACH VIEW ─ original grid ─ */
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* ══ Left col (main content) ══════════════════════════ */}
        <div className="lg:col-span-2 space-y-5">

          {/* Setup Notice — amber warning card when team setup is
              incomplete. Renders null when team.setupComplete is true,
              so cost is zero for fully-set-up teams. Restored May
              2026: the component existed in the codebase but was
              orphan (not mounted anywhere) post-Phase-1, meaning a
              coach who skipped wizard sections had no visible
              reminder to come back and finish. Coach-only via the
              canEdit gate (parents see no setup chrome). */}
          {canEdit && team && !team.setupComplete && (
            <TeamSetupNotice team={team} />
          )}

          {/* Smart Actions — the assistant-coach surface restored
              May 2026 (Phase 1 had unintentionally dropped this when
              Dashboard.jsx was unmounted). Surfaces actionable
              prompts for the active season: closing soon, unpaid
              registrations, missing documents. Renders null when
              there's nothing actionable, so the layout collapses
              cleanly for new/inactive teams. */}
          {/* Disengaged Parent smart action — sits above TeamSmartActions
              per architecture §5.2 / wireframe §3. Renders null when 0
              parents qualify. See features/disengaged-parent/. */}
          <DisengagedParentCard teamId={teamId} />

          <TeamSmartActions
            teamId={teamId}
            activeSeason={activeSeason}
            activeRegCount={activeRegCount}
            unpaidCount={unpaidCount}
            missingDocsCount={missingDocsCount}
            daysUntilClose={daysUntilClose}
          />

          {/* Active season card or CTA */}
          {activeSeason ? (
            <ActiveSeasonCard
              season={activeSeason}
              regCount={activeRegCount}
              revenue={activeRevenue}
              teamId={teamId}
            />
          ) : (
            <div className="rounded-2xl border border-dashed border-[#b5f03d]/40 bg-white p-8 text-center">
              <div className="mx-auto h-10 w-10 rounded-full bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 flex items-center justify-center mb-3">
                <CalendarDaysIcon className="h-5 w-5 text-[#5a8a1f]" />
              </div>
              <p className="text-sm font-semibold text-[#0d1f14] mb-1">{t('teamDetail.noOpenSeason')}</p>
              <p className="text-xs text-zinc-500 mb-4">{t('teamDetail.createSeasonHint')}</p>
              {canEdit && (
                <Link
                  to="/seasons/create"
                  className="inline-flex items-center gap-1.5 rounded-[10px] bg-[#b5f03d] hover:bg-[#94cc28] px-3 py-1.5 text-xs font-bold uppercase text-[#0d1f14] transition-colors"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                >
                  <PlusIcon className="h-3.5 w-3.5" /> {t('teamDetail.createSeason')}
                </Link>
              )}
            </div>
          )}

          {/* Recent Activity — coach-only. The "assistant coach"
              answer to "what's been happening with my team?". v1
              ships registrations only (paid + pending); future
              versions add roster changes, schedule changes, weather
              alerts, broadcasts. Renders null on inactive teams so
              the layout doesn't carry a "nothing happening" empty
              state. Restored May 2026 from the unmounted Dashboard. */}
          {canEdit && (
            <TeamActivityFeed teamId={teamId} activeSeason={activeSeason} />
          )}

          {/* Communication Hub + Team Tools — pre-activation (this team
              has never sent a message) both collapse behind a single
              "More tools" toggle so a brand-new coach sees only the
              checklist and the season card, not the whole machine.
              hasMessage === null (still checking) renders the full
              layout so activated coaches never watch their tools
              disappear. */}
          {canEdit && hasMessage === false && !showMoreTools ? (
            <button
              type="button"
              onClick={() => setShowMoreTools(true)}
              className="w-full rounded-2xl border border-dashed border-zinc-200 bg-white px-4 py-3 text-xs font-semibold text-zinc-500 hover:border-[#b5f03d]/40 hover:text-[#5a8a1f] transition-colors flex items-center justify-center gap-1.5"
            >
              <ChevronDownIcon className="h-3.5 w-3.5" />
              {t('teamDetail.moreTools')}
            </button>
          ) : (
          <>
          {/* Communication Hub — coach-only. Restored May 2026 from
              the unmounted Dashboard.jsx: Match Recap banner, Quick
              Compose tiles, and Recent Broadcasts preview. Sits
              between the season block and Team Tools because it's
              a daily-use surface (compose / review messages) rather
              than an admin tile. */}
          {canEdit && (
            <TeamCommunicationHub teamId={teamId} />
          )}

          {/* Quick navigation */}
          <div className="space-y-2">
            <SectionLabel className="px-0.5">{t('teamDetail.teamTools')}</SectionLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <QuickLink
                to={teamId ? `/teams/${teamId}/chat` : '/messages'}
                icon={ChatBubbleLeftRightIcon}
                label={t('teamDetail.messageTeam')}
                sub={t('teamDetail.messageTeamSub')}
              />
              <QuickLink
                to="/seasons"
                icon={CalendarDaysIcon}
                label={t('teamDetail.seasons')}
                sub={t('teamDetail.seasonsSub')}
              />
              <QuickLink
                to={teamId ? `/teams/${teamId}/roster` : '/teams'}
                icon={UserGroupIcon}
                label={t('teamDetail.roster')}
                sub={rosterCount != null ? t('teamDetail.playerCount', { count: rosterCount }) : t('teamDetail.managePlayers')}
              />
              <QuickLink
                to={teamId ? `/teams/${teamId}/fields` : '/teams'}
                icon={MapPinIcon}
                label={t('teamDetail.fields')}
                sub={t('teamDetail.fieldsSub')}
              />
              <QuickLink
                to="/reports/financial"
                icon={ChartBarIcon}
                label={t('teamDetail.financialReports')}
                sub={t('teamDetail.financialReportsSub')}
              />
              <QuickLink
                to="/tryouts"
                icon={TrophyIcon}
                label={t('teamDetail.tryouts')}
                sub={t('teamDetail.tryoutsSub')}
              />
              {/* AI Messages — the marketing site features this prominently
                  ("AI message composer drafts in your voice"). Was on the
                  sidebar pre-Phase-2 and went orphan when team-scoped
                  items moved off the sidebar; restored as a Tool tile so
                  the marketing claim remains true. */}
              <QuickLink
                to={teamId ? `/teams/${teamId}/messages` : '/teams'}
                icon={SparklesIcon}
                label={t('teamDetail.aiMessages')}
                sub={t('teamDetail.aiMessagesSub')}
              />
              {/* Team Invites — for onboarding new staff/parents to the
                  team. Onboarding checklist links here for first-time
                  setup, but coaches need an ongoing path too (new
                  assistant joins mid-season, parent transfers in, etc.). */}
              <QuickLink
                to={teamId ? `/teams/${teamId}/invites` : '/teams'}
                icon={EnvelopeOpenIcon}
                label={t('teamDetail.teamInvites')}
                sub={t('teamDetail.teamInvitesSub')}
              />
              {team.clubId && isClub && (
                <QuickLink
                  to={`/clubs/${team.clubId}/programs`}
                  icon={RectangleGroupIcon}
                  label={t('teamDetail.programs')}
                  sub={t('teamDetail.programsSub')}
                />
              )}
            </div>
          </div>
          </>
          )}

        </div>

        {/* ══ Right col (sidebar) ══════════════════════════════ */}
        <div className="space-y-5">

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 lg:grid-cols-1 lg:gap-3">
            <MiniStat
              label={t('teamDetail.roster')}
              value={rosterCount ?? '—'}
              icon={UserGroupIcon}
              tone="lime"
              to={teamId ? `/teams/${teamId}/roster` : undefined}
            />
            <MiniStat
              label={t('teamDetail.registered')}
              value={activeSeason ? (activeRegCount ?? '—') : '—'}
              icon={CalendarDaysIcon}
              tone="emerald"
              to={activeSeason ? `/seasons/${activeSeason.id}/registrations` : undefined}
            />
            <MiniStat
              label={t('teamDetail.unpaid')}
              value={activeSeason ? (unpaidCount ?? '—') : '—'}
              icon={ExclamationCircleIcon}
              tone="amber"
              to={activeSeason ? `/seasons/${activeSeason.id}/registrations` : undefined}
            />
          </div>


        </div>
      </div>
      )}
    </div>
    </div>
  );
}
